import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { channelWa, flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { TabHelp } from '../../../app/guides/TabHelp';
import { useT } from '../../../i18n/I18nProvider';
import { useBots, botName as nameOf } from '../../../lib/bots';
import { formatRelative } from '../../../lib/format';
import { StatusPill } from '../../../components/panel';
import { CoachPanel } from './Coach';
import { ENTRY_KINDS, triggerType, type EntryKind } from '../entry-templates';
import { LooseEndsBar } from '../components/LooseEndsBar';

/**
 * The bot at a glance: who it is, which entry points are set up, which
 * channels it answers on, and the questions it could not answer, with the
 * fix one click away. This is what /flows shows before a flow is picked.
 */
/** The overview's tabs (8 October 2026), each with a count when something there needs the business's attention. */
const TABS = ['entries', 'channels', 'questions', 'coach'] as const;
type Tab = (typeof TABS)[number];

export default function FlowsOverview() {
  const t = useT();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'entries';
  const choose = (next: string) => { const p = new URLSearchParams(params); p.set('tab', next); setParams(p, { replace: true }); };
  const openQuestions = useQuery({ queryKey: ['bot-questions', 'unanswered'], queryFn: () => flows.listBotQuestions({ unanswered: true, open: true, limit: 20 }) });
  // The chatbot being looked at: the only one for most organisations, the one
  // chosen in the sidebar for an organisation with several.
  const { active, owns, multi } = useBots();
  const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows() });
  const numbers = useQuery({ queryKey: ['phone-numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const sites = useQuery({ queryKey: ['web-sites'], queryFn: () => flows.listWebSites(), staleTime: 60_000 });
  const summary = useQuery({ queryKey: ['bot-questions-summary'], queryFn: () => flows.getBotQuestionSummary() });
  const coach = useQuery({ queryKey: ['bot-coach'], queryFn: () => flows.getBotCoach(), retry: false });
  const all = useMemo(() => (list.data?.data ?? []).filter(owns), [list.data, owns]);
  const entries = useMemo(() => {
    const map = new Map<EntryKind, flows.Flow>();
    for (const f of all) {
      const type = triggerType(f.definition) as EntryKind;
      if (ENTRY_KINDS.includes(type) && !map.has(type)) map.set(type, f);
    }
    return map;
  }, [all]);
  const botName = nameOf(active, t('settings.assistant.defaultName'));
  const questionCount = openQuestions.data?.data.length ?? 0;
  const attention: Record<Tab, string | null> = {
    // Entry points not set up yet: each is a way a conversation starts that nothing answers.
    entries: list.data ? String(ENTRY_KINDS.filter((k) => !entries.has(k)).length) : null,
    // Nowhere to answer at all: no WhatsApp number and no website chat.
    channels: numbers.data && sites.data && (numbers.data.data.length + sites.data.data.length) === 0 ? '1' : null,
    // The list is read twenty at a time.
    questions: questionCount >= 20 ? '20+' : questionCount > 0 ? String(questionCount) : null,
    // The coach's suggestions waiting for Apply or Dismiss.
    coach: coach.data && coach.data.data.open_suggestions > 0 ? String(coach.data.data.open_suggestions) : null
  };
  const s = summary.data?.data;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('flows.ws.title', { name: botName })}
        description={t('flows.ws.description')}
        withDivider={false}
        actions={(
          <Group gap="xs">
            <HelpButton guide="automation" />
            <Button size="sm" onClick={() => nav('/flows/ideas')} data-guide="flows-suggest" data-testid="flows-suggest-ai-header">{t('flows.actions.suggestWithAi')}</Button>
            <Button size="sm" variant="default" data-guide="flows-persona" onClick={() => nav(multi && active ? `/settings/assistant/${active.id}` : '/settings/assistant')}>{t('flows.ws.persona')}</Button>
          </Group>
        )}
      />

      {/* Steps in switched-on flows that lead nowhere, with Review; the notice email links to /flows?loose_ends=1 (2 November 2026). */}
      <LooseEndsBar />

      <div data-guide="flows-tabs">
        <Tabs value={tab} onChange={choose}>
          <Tabs.List>
            {TABS.map((x) => (
              <span key={x} className="rr-tabgroup">
                <Tabs.Tab value={x} rightSection={attention[x] && attention[x] !== '0' ? (
                  <span className="rr-tabcount" aria-label={t('flows.ws.tabs.attention', { count: attention[x] })}>{attention[x]}</span>
                ) : undefined}>
                  {t(`flows.ws.tabs.${x}`)}
                </Tabs.Tab>
                <TabHelp guide="automation" titleKey={`flows.ws.tabs.${x}`} bodyKey={`guide.automation.tab.flows.${x}`} />
              </span>
            ))}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Entry points: the shape of the bot. */}
      {tab === 'entries' && (
      <Card withBorder data-guide="flows-entries">
        <Stack gap="sm">
          <Title order={4}>{t('flows.ws.entryPoints')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.ws.entryPointsHint')}</Text>
          <div className="rr-flows-ws__entries">
            {ENTRY_KINDS.map((kind) => {
              const f = entries.get(kind);
              return (
                <div key={kind} className="rr-flows-ws__entry">
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Text role="bodySmall" fw={700}>{t(`flows.entry.${kind}.name`)}</Text>
                    {f ? <StatusPill tone={f.is_active ? 'active' : 'paused'}>{f.is_active ? t('status.active') : t('status.inactive')}</StatusPill> : <Badge size="xs" variant="light">{t('flows.ws.notSetUp')}</Badge>}
                  </Group>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t(`flows.entry.${kind}.hint`)}</Text>
                  {f
                    ? <Button size="xs" variant="subtle" onClick={() => nav(`/flows/${f.id}/builder`)}>{t('flows.ws.openBuilder')}</Button>
                    : kind === 'default'
                      ? <Button size="xs" variant="subtle" onClick={() => nav('/knowledge')}>{t('flows.ws.setUpOnKnowledge')}</Button>
                      : <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.ws.setUpFromSidebar')}</Text>}
                </div>
              );
            })}
            <div className="rr-flows-ws__entry">
              <Text role="bodySmall" fw={700}>{t('flows.entry.noAgent.name')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.entry.noAgent.hint')}</Text>
            </div>
          </div>
        </Stack>
      </Card>
      )}

      {/* Channels. */}
      {tab === 'channels' && (
      <Card withBorder data-guide="flows-channels">
        <Group justify="space-between" align="center" wrap="wrap">
          <Stack gap={2}>
            <Title order={4}>{t('flows.ws.channels')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {t('flows.ws.channelsLine', { numbers: numbers.data?.data.length ?? 0, sites: sites.data?.data.length ?? 0 })}
            </Text>
          </Stack>
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={() => nav('/numbers')}>{t('nav.numbers')}</Button>
            <Button size="xs" variant="default" onClick={() => nav('/knowledge')}>{t('nav.knowledge')}</Button>
          </Group>
        </Group>
      </Card>
      )}

      {/* Questions the bot could not answer. */}
      {tab === 'questions' && (
      <Card withBorder data-guide="flows-questions">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4}>{t('flows.ws.questions')}</Title>
            {s && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('flows.ws.questionsSummary', { asked: s.asked, answered: s.answered, unanswered: s.unanswered })}
              </Text>
            )}
          </Group>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.ws.questionsHint')}</Text>
          <QuestionsList />
        </Stack>
      </Card>
      )}

      {/* The coach: reviews conversations and suggests fixes. */}
      {tab === 'coach' && <CoachPanel />}
    </Stack>
  );
}

/** The unanswered questions, each with the fix right there. */
function QuestionsList() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [answering, setAnswering] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const open = useQuery({ queryKey: ['bot-questions', 'unanswered'], queryFn: () => flows.listBotQuestions({ unanswered: true, open: true, limit: 20 }) });
  const rows = open.data?.data ?? [];
  const resolve = useMutation({
    mutationFn: ({ id, resolution, answer }: { id: string; resolution: string; answer?: string }) => flows.resolveBotQuestion(id, { resolution, answer }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['bot-questions'] });
      qc.invalidateQueries({ queryKey: ['bot-questions-summary'] });
      qc.invalidateQueries({ queryKey: ['kb-documents'] });
      setAnswering(null);
      setDraft('');
      notifySuccess(v.resolution === 'answer_added' ? t('flows.ws.answerAdded') : t('flows.ws.questionClosed'), t('flows.ws.questions'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('flows.ws.questions'))
  });
  if (open.error) return <Alert color="error" role="alert">{(open.error as Error).message}</Alert>;
  if (!open.isPending && rows.length === 0) return <EmptyState title={t('flows.ws.noMisses')} description={t('flows.ws.noMissesHint')} />;
  return (
    <div className="rr-flows-ws__questions">
      {rows.map((q) => (
        <div key={q.id} className="rr-flows-ws__question">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Text role="bodySmall" fw={650}>“{q.question}”</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {formatRelative(q.created_at)} · {q.channel}{q.contact_id ? ` · ${q.contact_id}` : ''}
                {q.flow_run_id && q.flow_id ? <> · <Link to={`/flows/${q.flow_id}?tab=runs`}>{t('flows.ws.viewRun')}</Link></> : null}
              </Text>
            </Stack>
            <Group gap={4} wrap="nowrap">
              <Button size="xs" variant={answering === q.id ? 'filled' : 'default'} onClick={() => { setAnswering(answering === q.id ? null : q.id); setDraft(''); }}>{t('flows.ws.addAnswer')}</Button>
              <Button size="xs" variant="subtle" onClick={() => resolve.mutate({ id: q.id, resolution: 'dismissed' })}>{t('flows.ws.dismiss')}</Button>
            </Group>
          </Group>
          {answering === q.id && (
            <Stack gap={6} className="rr-flows-ws__answer">
              <Textarea
                label={t('flows.ws.answerLabel')}
                description={t('flows.ws.answerHint')}
                value={draft}
                minRows={3}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="subtle" onClick={() => nav('/knowledge')}>{t('flows.ws.addDocumentInstead')}</Button>
                <Button size="xs" disabled={draft.trim().length < 3} loading={resolve.isPending} onClick={() => resolve.mutate({ id: q.id, resolution: 'answer_added', answer: draft.trim() })}>{t('flows.ws.saveAnswer')}</Button>
              </Group>
            </Stack>
          )}
        </div>
      ))}
    </div>
  );
}
